/**
 * Load optional theme modules only when their elements are present.
 * Observing inserted subtrees also covers sections, filters and Quick Add.
 * This document-level loader does not own any interactive component state.
 *
 * @param {Document} root
 * @param {(url: string) => Promise<unknown>} [importFeature]
 * @returns {(() => void) | undefined} Stops watching this document.
 */
export function loadSectionFeatures(root, importFeature = (url) => import(url)) {
  const manifest = root.getElementById('sa-feature-manifest');
  if (!manifest) return;

  /** @type {{ selector: string, url: string }[]} */
  const features = JSON.parse(manifest.textContent || '[]');
  const pending = new Set(features);

  /** @param {Document | Element} subtree */
  function scan(subtree) {
    for (const feature of pending) {
      if (
        !(subtree instanceof Element && subtree.matches(feature.selector)) &&
        !subtree.querySelector(feature.selector)
      ) continue;

      // Remove before importing: repeated DOM changes cannot duplicate a load.
      pending.delete(feature);
      importFeature(feature.url).catch((error) => {
        // A later section insertion may retry a transient network failure.
        pending.add(feature);
        console.error('SanoAlint: optional theme module could not load.', error);
      });
    }
  }

  const observer = new MutationObserver((records) => {
    if (!pending.size) return;
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node instanceof Element) scan(node);
      }
    }
  });

  observer.observe(root.documentElement, { childList: true, subtree: true });
  scan(root);
  return () => observer.disconnect();
}

if (typeof document !== 'undefined') loadSectionFeatures(document);
